import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { FilterCellsProps, IconNames } from "@tvg/design-system";
import {
  WagerType,
  WagerTypeCodesEnum,
  WagerTypesGroup
} from "@tvg/ts-types/Wager";
import { getBlacklistedWagerTypes } from "@tvg/pp/src/store/selectors";
import { RaceWagerType } from "@tvg/ts-types/Race";
import { getURPWagerPadMessages } from "../redux/selectors";

export enum GroupFilterEnum {
  STRAIGHTS = "straights",
  EXOTICS = "exotics",
  MULTI = "multi"
}

type GroupWagerTypes = Record<GroupFilterEnum, WagerType[]>;

type FilterOptions = FilterCellsProps["filterOptions"];

export const formatLabel = (name: string, isExotic = false) => {
  if (isExotic) {
    const parts = name.split(" ");
    return parts.length === 1 ? name : parts.slice(1).join(" ");
  }
  if (/^Pick \d$/.test(name)) {
    return name.replace("Pick ", "P");
  }
  return name;
};

const useGroupWagerTypes = (
  wagerTypes?: RaceWagerType[],
  cppPromoObj?: {
    promosBetType: (string | undefined)[];
    isOptedin: boolean;
  },
  hasRun?: React.MutableRefObject<boolean>
) => {
  const [groupOptions, setGroupOptions] = useState<FilterOptions>([]);
  const [groupWagerTypes, setGroupWagerTypes] = useState<GroupWagerTypes>({
    straights: [],
    exotics: [],
    multi: []
  });

  const wagerPadMessages = useSelector(getURPWagerPadMessages);
  const blacklistedTypes = useSelector(getBlacklistedWagerTypes);

  const getIconNameFromCodes = (
    code: (string | undefined)[] | undefined
  ): IconNames | undefined => {
    const hasCPPPromo = cppPromoObj?.promosBetType.some((wagerTypeCode) =>
      code?.includes(wagerTypeCode as unknown as WagerTypeCodesEnum)
    );

    if (!hasCPPPromo) return undefined;

    const isOptedIn = cppPromoObj?.isOptedin;

    if (!hasRun?.current && hasCPPPromo) {
      return isOptedIn ? "optedIn" : "promo";
    }
    return undefined;
  };

  useEffect(() => {
    if (wagerTypes) {
      const groupTypes = wagerTypes.reduce(
        (group: GroupWagerTypes, current: RaceWagerType) => {
          const isTypeAvailable = !(
            blacklistedTypes?.blacklistedBets ?? []
          ).includes(current.type.code);
          if (!isTypeAvailable) {
            return group;
          }
          if (current.legCount > 1) {
            group.multi.push({
              ...current.type,
              unitedWagerTypeCode: current.unitedWagerTypeCode
            });
          } else if (
            WagerTypesGroup.WIN_PLACE_SHOW.includes(current.group.id)
          ) {
            group.straights.push({
              ...current.type,
              unitedWagerTypeCode: current.unitedWagerTypeCode
            });
          } else {
            group.exotics.push({
              ...current.type,
              unitedWagerTypeCode: current.unitedWagerTypeCode
            });
          }
          return group;
        },
        {
          straights: [],
          exotics: [],
          multi: []
        }
      );

      setGroupWagerTypes(groupTypes);

      const filteredOptions = Object.entries(groupTypes).reduce(
        (cells: FilterOptions, [key, value]) => {
          if (key === GroupFilterEnum.EXOTICS) {
            value.forEach((wagerType: WagerType) => {
              const iconName = getIconNameFromCodes?.([
                wagerType.unitedWagerTypeCode
              ]);
              const option = {
                label: wagerType.name,
                value: wagerType.code,
                iconName
              };

              const typeName = wagerType.name.split(" ");
              const groupAdded = cells.find(
                (cell) => cell.label === typeName[0]
              );
              if (!groupAdded) {
                cells.push(option);
              }
            });
          } else if (value.length > 0) {
            const label =
              key === GroupFilterEnum.MULTI
                ? wagerPadMessages.wagerTypeSelector.multi
                : wagerPadMessages.wagerTypeSelector.straights;

            const iconName = getIconNameFromCodes?.(
              value.map((item) => item.unitedWagerTypeCode)
            );

            const option = {
              label,
              value: key,
              iconName
            };
            cells.push(option);
          }
          return cells;
        },
        []
      );
      setGroupOptions(filteredOptions);
    }
  }, [
    JSON.stringify(blacklistedTypes),
    wagerTypes,
    cppPromoObj?.promosBetType
  ]);

  const getModifierOptions = useCallback(
    (mainGroup: string) => {
      if (
        mainGroup === GroupFilterEnum.STRAIGHTS ||
        mainGroup === GroupFilterEnum.MULTI
      ) {
        // eslint-disable-next-line security/detect-object-injection
        return groupWagerTypes[mainGroup].map((wagerType) => ({
          label: formatLabel(wagerType.name),
          value: wagerType.code
        }));
      }
      const mainWagerType = groupWagerTypes.exotics.find(
        (wagerType) => wagerType.code === mainGroup
      );
      return groupWagerTypes.exotics
        .filter((wagerType) =>
          wagerType.name.startsWith(mainWagerType?.name || mainGroup)
        )
        .map((wagerType) => ({
          label: formatLabel(wagerType.name, true),
          value: wagerType.code
        }));
    },
    [groupWagerTypes]
  );

  return { groupOptions, getModifierOptions };
};

export default useGroupWagerTypes;
